import React, { useState } from 'react';
import { useAppSelector } from '../../redux/hooks';
import {
  useListSiteContractsQuery,
  useGetContractQuery,
  useGetContractTermsQuery,
  useGetContractKeyDatesQuery,
  useGetContractKeyTermsQuery,
  useGetContractClausesQuery,
  useGetContractPartiesQuery,
  useGetContractQAResultsQuery,
  useGetUpcomingDatesQuery,
  useListTemplatesQuery,
  useGetTemplateQuery,
  useUpdateTemplateMutation,
} from '../../redux/api/contractsApi';
import type {
  Contract,
  ExtractedTerm,
  ContractKeyDate,
  ContractKeyTerm,
  ContractClause,
  ContractParty,
  ContractQAResult,
  ExtractionTemplate,
  ExtractionTemplateDetail,
} from '../../redux/api/contractsApi';
import {
  FileText,
  Calendar,
  DollarSign,
  Shield,
  Users,
  ChevronRight,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Clock,
  LayoutDashboard,
  Settings,
  Save,
} from 'lucide-react';

// --- Contract type labels & colors ---

const CONTRACT_TYPE_CONFIG: Record<string, { label: string; color: string }> = {
  oma: { label: 'O&M Agreement', color: '#3b82f6' },
  ama: { label: 'Asset Management', color: '#8b5cf6' },
  ppa: { label: 'PPA', color: '#10b981' },
  epc: { label: 'EPC', color: '#f59e0b' },
  warranty: { label: 'Warranty', color: '#ef4444' },
  lease: { label: 'Lease', color: '#6366f1' },
  interconnection: { label: 'Interconnection', color: '#14b8a6' },
  other: { label: 'Other', color: '#6b7280' },
};

const STATUS_COLORS: Record<string, string> = {
  draft: '#9ca3af',
  active: '#10b981',
  expired: '#f59e0b',
  terminated: '#ef4444',
  superseded: '#6b7280',
};

function getTypeConfig(type: string) {
  return CONTRACT_TYPE_CONFIG[type] || CONTRACT_TYPE_CONFIG.other;
}

// --- Sub-components ---

function ContractCard({
  contract,
  isSelected,
  onClick,
}: {
  contract: Contract;
  isSelected: boolean;
  onClick: () => void;
}) {
  const typeConfig = getTypeConfig(contract.contract_type);
  const statusColor = STATUS_COLORS[contract.status] || '#6b7280';

  return (
    <div
      onClick={onClick}
      className={`p-4 rounded-lg border cursor-pointer transition-all ${
        isSelected
          ? 'border-[var(--color-border-active)] bg-[var(--color-bg-tertiary)]'
          : 'border-[var(--color-border-primary)] bg-[var(--color-bg-secondary)] hover:border-[var(--color-border-hover)]'
      }`}
    >
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2">
          <span
            className="px-2 py-0.5 rounded text-xs font-medium text-white"
            style={{ backgroundColor: typeConfig.color }}
          >
            {typeConfig.label}
          </span>
          <span
            className="px-2 py-0.5 rounded text-xs font-medium"
            style={{ color: statusColor, border: `1px solid ${statusColor}` }}
          >
            {contract.status}
          </span>
        </div>
        <ChevronRight size={16} className="text-[var(--color-text-tertiary)]" />
      </div>
      <h3 className="text-sm font-medium text-[var(--color-text-primary)] mb-1 line-clamp-2">
        {contract.title}
      </h3>
      <div className="flex items-center gap-3 text-xs text-[var(--color-text-secondary)]">
        {contract.effective_date && (
          <span className="flex items-center gap-1">
            <Calendar size={12} />
            {new Date(contract.effective_date).toLocaleDateString()}
          </span>
        )}
        {contract.expiration_date && (
          <span className="flex items-center gap-1">
            <Clock size={12} />
            {new Date(contract.expiration_date).toLocaleDateString()}
          </span>
        )}
      </div>
    </div>
  );
}

function SectionHeader({ icon: Icon, title, count }: { icon: React.ElementType; title: string; count?: number }) {
  return (
    <div className="flex items-center gap-2 mb-3 pb-2 border-b border-[var(--color-border-primary)]">
      <Icon size={16} className="text-[var(--color-text-secondary)]" />
      <h3 className="text-sm font-semibold text-[var(--color-text-primary)]">{title}</h3>
      {count !== undefined && (
        <span className="px-1.5 py-0.5 rounded-full text-xs bg-[var(--color-bg-tertiary)] text-[var(--color-text-secondary)]">
          {count}
        </span>
      )}
    </div>
  );
}

function TermsTable({ terms }: { terms: ExtractedTerm[] }) {
  if (!terms.length) return <p className="text-xs text-[var(--color-text-tertiary)] italic">No terms extracted</p>;

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b border-[var(--color-border-primary)]">
            <th className="text-left py-2 pr-3 font-medium text-[var(--color-text-secondary)]">Field</th>
            <th className="text-left py-2 pr-3 font-medium text-[var(--color-text-secondary)]">Value</th>
            <th className="text-right py-2 font-medium text-[var(--color-text-secondary)] w-16">Conf</th>
          </tr>
        </thead>
        <tbody>
          {terms.map((term) => {
            const value = term.value_text || (term.value_number != null ? String(term.value_number) : term.value_date || '-');
            return (
              <tr key={term.id} className="border-b border-[var(--color-border-secondary)]">
                <td className="py-2 pr-3 text-[var(--color-text-secondary)] font-medium whitespace-nowrap">
                  {term.field_label}
                  {term.needs_review && (
                    <AlertTriangle size={12} className="inline ml-1 text-yellow-500" />
                  )}
                </td>
                <td className="py-2 pr-3 text-[var(--color-text-primary)] max-w-xs truncate">{value}</td>
                <td className="py-2 text-right">
                  {term.confidence != null && (
                    <span className={`font-mono ${term.confidence >= 0.8 ? 'text-green-400' : term.confidence >= 0.5 ? 'text-yellow-400' : 'text-red-400'}`}>
                      {(term.confidence * 100).toFixed(0)}%
                    </span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function KeyDatesSection({ dates }: { dates: ContractKeyDate[] }) {
  if (!dates.length) return null;
  return (
    <div className="space-y-2">
      {dates.map((d) => (
        <div key={d.id} className="flex items-center justify-between py-1.5 px-2 rounded bg-[var(--color-bg-tertiary)]">
          <div className="flex items-center gap-2">
            <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-500/20 text-blue-400 uppercase">
              {d.date_type}
            </span>
            <span className="text-xs text-[var(--color-text-primary)]">{d.label}</span>
          </div>
          <span className="text-xs font-mono text-[var(--color-text-secondary)]">
            {new Date(d.date_value + 'T00:00:00').toLocaleDateString()}
          </span>
        </div>
      ))}
    </div>
  );
}

function KeyTermsSection({ terms }: { terms: ContractKeyTerm[] }) {
  if (!terms.length) return null;
  return (
    <div className="space-y-2">
      {terms.map((t) => {
        const value = t.value_number != null
          ? `${t.currency || ''}${t.value_number.toLocaleString()}${t.unit ? ` ${t.unit}` : ''}`
          : t.value_text || '-';
        return (
          <div key={t.id} className="flex items-center justify-between py-1.5 px-2 rounded bg-[var(--color-bg-tertiary)]">
            <div className="flex items-center gap-2">
              <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-green-500/20 text-green-400 uppercase">
                {t.term_type}
              </span>
              <span className="text-xs text-[var(--color-text-primary)]">{t.label}</span>
            </div>
            <span className="text-xs font-medium text-[var(--color-text-primary)]">{value}</span>
          </div>
        );
      })}
    </div>
  );
}

function ClausesSection({ clauses }: { clauses: ContractClause[] }) {
  if (!clauses.length) return null;
  return (
    <div className="space-y-3">
      {clauses.map((c) => (
        <div key={c.id} className="p-3 rounded bg-[var(--color-bg-tertiary)] border border-[var(--color-border-secondary)]">
          <div className="flex items-center gap-2 mb-2">
            <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-purple-500/20 text-purple-400 uppercase">
              {c.clause_type.replace(/_/g, ' ')}
            </span>
            <span className="text-xs font-medium text-[var(--color-text-primary)]">{c.label}</span>
          </div>
          {c.condition && (
            <p className="text-xs text-[var(--color-text-secondary)] mb-1">
              <span className="font-medium text-yellow-400">IF:</span> {c.condition}
            </p>
          )}
          {c.consequence && (
            <p className="text-xs text-[var(--color-text-secondary)]">
              <span className="font-medium text-green-400">THEN:</span> {c.consequence}
            </p>
          )}
        </div>
      ))}
    </div>
  );
}

function PartiesSection({ parties }: { parties: ContractParty[] }) {
  if (!parties.length) return null;
  return (
    <div className="flex flex-wrap gap-2">
      {parties.map((p) => (
        <div key={p.id} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[var(--color-bg-tertiary)] border border-[var(--color-border-secondary)]">
          <Users size={14} className="text-[var(--color-text-tertiary)]" />
          <div>
            <div className="text-xs font-medium text-[var(--color-text-primary)]">{p.name}</div>
            <div className="text-[10px] text-[var(--color-text-tertiary)] uppercase">{p.role}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

function QAResultsSection({ results }: { results: ContractQAResult[] }) {
  if (!results.length) return null;
  return (
    <div className="space-y-2">
      {results.map((r) => (
        <div key={r.id} className="flex items-start gap-2 py-1.5 px-2 rounded bg-[var(--color-bg-tertiary)]">
          {r.passed === true ? (
            <CheckCircle size={14} className="text-green-400 mt-0.5 flex-shrink-0" />
          ) : r.passed === false ? (
            <XCircle size={14} className="text-red-400 mt-0.5 flex-shrink-0" />
          ) : (
            <AlertTriangle size={14} className="text-yellow-400 mt-0.5 flex-shrink-0" />
          )}
          <div className="min-w-0">
            <div className="text-xs font-medium text-[var(--color-text-primary)]">
              {r.check_label || r.check_key}
              {r.severity && (
                <span className={`ml-2 text-[10px] ${r.severity === 'error' ? 'text-red-400' : r.severity === 'warning' ? 'text-yellow-400' : 'text-gray-400'}`}>
                  {r.severity}
                </span>
              )}
            </div>
            {r.reasoning && (
              <p className="text-[11px] text-[var(--color-text-secondary)] mt-0.5 line-clamp-2">{r.reasoning}</p>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

// --- Contract Detail Panel ---

function ContractDetailPanel({ contractId }: { contractId: string }) {
  const { data: contract, isLoading } = useGetContractQuery({ contractId });
  const { data: termsData } = useGetContractTermsQuery({ contractId });
  const { data: datesData } = useGetContractKeyDatesQuery({ contractId });
  const { data: keyTermsData } = useGetContractKeyTermsQuery({ contractId });
  const { data: clausesData } = useGetContractClausesQuery({ contractId });
  const { data: partiesData } = useGetContractPartiesQuery({ contractId });
  const { data: qaData } = useGetContractQAResultsQuery({ contractId });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[var(--color-text-secondary)]" />
      </div>
    );
  }

  if (!contract) {
    return <div className="p-4 text-sm text-[var(--color-text-tertiary)]">Contract not found</div>;
  }

  const typeConfig = getTypeConfig(contract.contract_type);

  return (
    <div className="h-full overflow-y-auto p-4 space-y-6">
      {/* Header */}
      <div>
        <div className="flex items-center gap-2 mb-2">
          <span className="px-2 py-0.5 rounded text-xs font-medium text-white" style={{ backgroundColor: typeConfig.color }}>
            {typeConfig.label}
          </span>
          <span className="px-2 py-0.5 rounded text-xs" style={{ color: STATUS_COLORS[contract.status], border: `1px solid ${STATUS_COLORS[contract.status]}` }}>
            {contract.status}
          </span>
        </div>
        <h2 className="text-base font-semibold text-[var(--color-text-primary)]">{contract.title}</h2>
        {contract.description && (
          <p className="text-xs text-[var(--color-text-secondary)] mt-1">{contract.description}</p>
        )}
        <div className="flex gap-4 mt-2 text-xs text-[var(--color-text-tertiary)]">
          <span>Terms: {contract.term_count}</span>
          <span>Dates: {contract.key_date_count}</span>
          <span>Clauses: {contract.clause_count}</span>
          <span>QA: {contract.qa_pass_count}/{contract.qa_pass_count + contract.qa_fail_count}</span>
        </div>
      </div>

      {/* Parties */}
      {partiesData && partiesData.items.length > 0 && (
        <div>
          <SectionHeader icon={Users} title="Parties" count={partiesData.items.length} />
          <PartiesSection parties={partiesData.items} />
        </div>
      )}

      {/* Extracted Terms */}
      {termsData && termsData.items.length > 0 && (
        <div>
          <SectionHeader icon={FileText} title="Extracted Terms" count={termsData.total} />
          <TermsTable terms={termsData.items} />
        </div>
      )}

      {/* Key Dates */}
      {datesData && datesData.items.length > 0 && (
        <div>
          <SectionHeader icon={Calendar} title="Key Dates" count={datesData.items.length} />
          <KeyDatesSection dates={datesData.items} />
        </div>
      )}

      {/* Key Terms */}
      {keyTermsData && keyTermsData.items.length > 0 && (
        <div>
          <SectionHeader icon={DollarSign} title="Key Terms" count={keyTermsData.items.length} />
          <KeyTermsSection terms={keyTermsData.items} />
        </div>
      )}

      {/* Clauses */}
      {clausesData && clausesData.items.length > 0 && (
        <div>
          <SectionHeader icon={Shield} title="Clauses" count={clausesData.items.length} />
          <ClausesSection clauses={clausesData.items} />
        </div>
      )}

      {/* QA Results */}
      {qaData && qaData.items.length > 0 && (
        <div>
          <SectionHeader icon={CheckCircle} title="QA Results" count={qaData.items.length} />
          <QAResultsSection results={qaData.items} />
        </div>
      )}
    </div>
  );
}

// --- Summary View ---

function ContractsSummaryView({ siteId }: { siteId: string }) {
  const { data: contractsList } = useListSiteContractsQuery({ siteId, limit: 100 }, { skip: !siteId });
  const { data: upcomingData } = useGetUpcomingDatesQuery({ siteId, days: 90 }, { skip: !siteId });

  const contracts = contractsList?.items || [];
  const upcoming = upcomingData?.items || [];

  // Group by type
  const byType: Record<string, number> = {};
  contracts.forEach((c) => {
    byType[c.contract_type] = (byType[c.contract_type] || 0) + 1;
  });

  // Group by status
  const byStatus: Record<string, number> = {};
  contracts.forEach((c) => {
    byStatus[c.status] = (byStatus[c.status] || 0) + 1;
  });

  return (
    <div className="h-full overflow-y-auto p-6 space-y-6">
      <h2 className="text-base font-semibold text-[var(--color-text-primary)]">Contracts Summary</h2>

      {/* Stats Grid */}
      <div className="grid grid-cols-4 gap-4">
        <div className="p-4 rounded-lg bg-[var(--color-bg-secondary)] border border-[var(--color-border-primary)]">
          <div className="text-2xl font-bold text-[var(--color-text-primary)]">{contracts.length}</div>
          <div className="text-xs text-[var(--color-text-secondary)]">Total Contracts</div>
        </div>
        <div className="p-4 rounded-lg bg-[var(--color-bg-secondary)] border border-[var(--color-border-primary)]">
          <div className="text-2xl font-bold text-green-400">{byStatus['active'] || 0}</div>
          <div className="text-xs text-[var(--color-text-secondary)]">Active</div>
        </div>
        <div className="p-4 rounded-lg bg-[var(--color-bg-secondary)] border border-[var(--color-border-primary)]">
          <div className="text-2xl font-bold text-yellow-400">{upcoming.length}</div>
          <div className="text-xs text-[var(--color-text-secondary)]">Upcoming Dates (90d)</div>
        </div>
        <div className="p-4 rounded-lg bg-[var(--color-bg-secondary)] border border-[var(--color-border-primary)]">
          <div className="text-2xl font-bold text-[var(--color-text-primary)]">{byStatus['expired'] || 0}</div>
          <div className="text-xs text-[var(--color-text-secondary)]">Expired</div>
        </div>
      </div>

      {/* By Type */}
      <div>
        <h3 className="text-sm font-semibold text-[var(--color-text-primary)] mb-3">By Type</h3>
        <div className="flex flex-wrap gap-2">
          {Object.entries(byType).map(([type, count]) => {
            const config = getTypeConfig(type);
            return (
              <div key={type} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[var(--color-bg-secondary)] border border-[var(--color-border-primary)]">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: config.color }} />
                <span className="text-xs font-medium text-[var(--color-text-primary)]">{config.label}</span>
                <span className="text-xs text-[var(--color-text-tertiary)]">{count}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Upcoming Dates */}
      {upcoming.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-[var(--color-text-primary)] mb-3">Upcoming Key Dates</h3>
          <div className="space-y-2">
            {upcoming.map((item, i) => (
              <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-[var(--color-bg-secondary)] border border-[var(--color-border-primary)]">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-500/20 text-blue-400 uppercase">
                      {item.key_date.date_type}
                    </span>
                    <span className="text-xs font-medium text-[var(--color-text-primary)]">{item.key_date.label}</span>
                  </div>
                  <span className="text-[11px] text-[var(--color-text-tertiary)]">
                    {item.key_date.contract_title}
                  </span>
                </div>
                <div className="text-right">
                  <div className="text-xs font-mono text-[var(--color-text-primary)]">
                    {new Date(item.key_date.date_value + 'T00:00:00').toLocaleDateString()}
                  </div>
                  <div className={`text-[10px] font-medium ${item.days_until <= 30 ? 'text-red-400' : item.days_until <= 60 ? 'text-yellow-400' : 'text-[var(--color-text-tertiary)]'}`}>
                    {item.days_until}d away
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// --- Template Editor View ---

function TemplateEditorView() {
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [editingPrompt, setEditingPrompt] = useState<'extraction' | 'evaluation' | null>(null);
  const [promptText, setPromptText] = useState('');

  const { data: templatesList } = useListTemplatesQuery({});
  const { data: templateDetail } = useGetTemplateQuery(
    { templateId: selectedTemplateId! },
    { skip: !selectedTemplateId },
  );
  const [updateTemplate, { isLoading: saving }] = useUpdateTemplateMutation();

  const templates = templatesList?.items || [];

  const handleSavePrompt = async () => {
    if (!selectedTemplateId || !editingPrompt) return;
    const body = editingPrompt === 'extraction'
      ? { extraction_prompt: promptText }
      : { evaluation_prompt: promptText };
    await updateTemplate({ templateId: selectedTemplateId, body });
    setEditingPrompt(null);
  };

  return (
    <div className="flex h-full">
      {/* Template List */}
      <div className="w-72 flex-shrink-0 border-r border-[var(--color-border-primary)] flex flex-col">
        <div className="p-3 border-b border-[var(--color-border-primary)]">
          <h3 className="text-sm font-semibold text-[var(--color-text-primary)]">Extraction Templates</h3>
        </div>
        <div className="flex-1 overflow-y-auto p-2 space-y-1">
          {templates.map((t) => (
            <div
              key={t.id}
              onClick={() => { setSelectedTemplateId(t.id); setEditingPrompt(null); }}
              className={`p-3 rounded cursor-pointer transition-all ${
                selectedTemplateId === t.id
                  ? 'bg-[var(--color-bg-tertiary)] border border-[var(--color-border-active)]'
                  : 'hover:bg-[var(--color-bg-tertiary)]'
              }`}
            >
              <div className="flex items-center gap-2">
                <span className="px-1.5 py-0.5 rounded text-[9px] font-medium text-white" style={{ backgroundColor: getTypeConfig(t.contract_type).color }}>
                  {t.contract_type.toUpperCase()}
                </span>
                <span className={`text-[9px] px-1 rounded ${t.is_active ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'}`}>
                  {t.is_active ? 'active' : 'inactive'}
                </span>
              </div>
              <div className="text-xs font-medium text-[var(--color-text-primary)] mt-1">{t.name}</div>
              <div className="text-[10px] text-[var(--color-text-tertiary)]">v{t.version} {t.tenant_id ? '(tenant)' : '(global)'}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Template Detail */}
      <div className="flex-1 min-w-0 overflow-y-auto">
        {templateDetail ? (
          <div className="p-4 space-y-4">
            <div>
              <h3 className="text-sm font-semibold text-[var(--color-text-primary)]">{templateDetail.name}</h3>
              {templateDetail.description && (
                <p className="text-xs text-[var(--color-text-secondary)] mt-1">{templateDetail.description}</p>
              )}
            </div>

            {/* Fields */}
            <div>
              <SectionHeader icon={FileText} title="Fields" count={templateDetail.fields.length} />
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-[var(--color-border-primary)]">
                      <th className="text-left py-2 pr-2 font-medium text-[var(--color-text-secondary)]">#</th>
                      <th className="text-left py-2 pr-2 font-medium text-[var(--color-text-secondary)]">Key</th>
                      <th className="text-left py-2 pr-2 font-medium text-[var(--color-text-secondary)]">Label</th>
                      <th className="text-left py-2 pr-2 font-medium text-[var(--color-text-secondary)]">Type</th>
                      <th className="text-left py-2 pr-2 font-medium text-[var(--color-text-secondary)]">Required</th>
                      <th className="text-left py-2 font-medium text-[var(--color-text-secondary)]">Cross-cut</th>
                    </tr>
                  </thead>
                  <tbody>
                    {templateDetail.fields.map((f, i) => (
                      <tr key={f.id} className="border-b border-[var(--color-border-secondary)]">
                        <td className="py-1.5 pr-2 text-[var(--color-text-tertiary)]">{i + 1}</td>
                        <td className="py-1.5 pr-2 font-mono text-[var(--color-text-primary)]">{f.field_key}</td>
                        <td className="py-1.5 pr-2 text-[var(--color-text-primary)]">{f.field_label}</td>
                        <td className="py-1.5 pr-2 text-[var(--color-text-secondary)]">{f.field_type}</td>
                        <td className="py-1.5 pr-2">{f.is_required ? <CheckCircle size={12} className="text-green-400" /> : null}</td>
                        <td className="py-1.5 text-[var(--color-text-tertiary)]">{f.cross_cut_target ? `${f.cross_cut_target}:${f.cross_cut_type}` : ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Prompts */}
            {['extraction', 'evaluation'].map((promptType) => {
              const currentText = promptType === 'extraction' ? templateDetail.extraction_prompt : templateDetail.evaluation_prompt;
              const isEditing = editingPrompt === promptType;

              return (
                <div key={promptType}>
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-xs font-semibold text-[var(--color-text-primary)] capitalize">{promptType} Prompt</h4>
                    {isEditing ? (
                      <div className="flex gap-2">
                        <button
                          onClick={handleSavePrompt}
                          disabled={saving}
                          className="flex items-center gap-1 px-2 py-1 rounded text-[10px] font-medium bg-green-500/20 text-green-400 hover:bg-green-500/30"
                        >
                          <Save size={10} /> {saving ? 'Saving...' : 'Save'}
                        </button>
                        <button
                          onClick={() => setEditingPrompt(null)}
                          className="px-2 py-1 rounded text-[10px] font-medium bg-[var(--color-bg-tertiary)] text-[var(--color-text-secondary)]"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => { setEditingPrompt(promptType as 'extraction' | 'evaluation'); setPromptText(currentText || ''); }}
                        className="px-2 py-1 rounded text-[10px] font-medium bg-[var(--color-bg-tertiary)] text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]"
                      >
                        Edit
                      </button>
                    )}
                  </div>
                  {isEditing ? (
                    <textarea
                      value={promptText}
                      onChange={(e) => setPromptText(e.target.value)}
                      className="w-full h-48 p-3 rounded text-xs font-mono bg-[var(--color-bg-tertiary)] border border-[var(--color-border-primary)] text-[var(--color-text-primary)] resize-y"
                    />
                  ) : (
                    <pre className="p-3 rounded text-[11px] font-mono bg-[var(--color-bg-tertiary)] text-[var(--color-text-secondary)] overflow-x-auto max-h-48 whitespace-pre-wrap">
                      {currentText || '(no prompt configured)'}
                    </pre>
                  )}
                </div>
              );
            })}

            {/* QA Checks */}
            {templateDetail.qa_checks.length > 0 && (
              <div>
                <SectionHeader icon={CheckCircle} title="QA Checks" count={templateDetail.qa_checks.length} />
                <div className="space-y-2">
                  {templateDetail.qa_checks.map((c) => (
                    <div key={c.id} className="p-2 rounded bg-[var(--color-bg-tertiary)]">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-mono text-[10px] text-[var(--color-text-tertiary)]">{c.check_key}</span>
                        <span className={`text-[9px] px-1 rounded ${c.severity === 'error' ? 'bg-red-500/20 text-red-400' : c.severity === 'warning' ? 'bg-yellow-500/20 text-yellow-400' : 'bg-gray-500/20 text-gray-400'}`}>
                          {c.severity}
                        </span>
                      </div>
                      <div className="text-xs text-[var(--color-text-primary)]">{c.check_label}</div>
                      <div className="text-[11px] text-[var(--color-text-secondary)] mt-1">{c.question_prompt}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="flex items-center justify-center h-full text-sm text-[var(--color-text-tertiary)]">
            Select a template to view details
          </div>
        )}
      </div>
    </div>
  );
}

// --- Main Tab Component ---

type TabView = 'summary' | 'contracts' | 'templates';

export default function ContractsTab() {
  const currentSite = useAppSelector((state) => state.site.currentSite);
  const siteId = currentSite?.site_id || '';
  const [activeView, setActiveView] = useState<TabView>('summary');
  const [selectedContractId, setSelectedContractId] = useState<string | null>(null);
  const [typeFilter, setTypeFilter] = useState<string>('');

  const { data: contractsList, isLoading, error } = useListSiteContractsQuery(
    { siteId, contractType: typeFilter || undefined, limit: 50 },
    { skip: !siteId || activeView === 'templates' }
  );

  if (!siteId) {
    return (
      <div className="flex items-center justify-center h-full text-sm text-[var(--color-text-tertiary)]">
        Select a site to view contracts
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-[var(--color-bg-primary)]">
      {/* View Toggle Header */}
      <div className="flex items-center gap-1 px-3 py-2 border-b border-[var(--color-border-primary)] bg-[var(--color-bg-secondary)]">
        {([
          { id: 'summary' as TabView, label: 'Summary', icon: LayoutDashboard },
          { id: 'contracts' as TabView, label: 'Contracts', icon: FileText },
          { id: 'templates' as TabView, label: 'Templates', icon: Settings },
        ]).map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => setActiveView(id)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium transition-colors ${
              activeView === id
                ? 'bg-[var(--color-bg-primary)] text-[var(--color-text-primary)] shadow-sm'
                : 'text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]'
            }`}
          >
            <Icon size={14} />
            {label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="flex-1 min-h-0">
        {activeView === 'summary' && <ContractsSummaryView siteId={siteId} />}
        {activeView === 'templates' && <TemplateEditorView />}
        {activeView === 'contracts' && (
          <div className="flex h-full">
            {/* Left: Contract List */}
            <div className="w-80 flex-shrink-0 border-r border-[var(--color-border-primary)] flex flex-col">
              <div className="p-3 border-b border-[var(--color-border-primary)]">
                <select
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                  className="w-full px-2 py-1.5 rounded text-xs bg-[var(--color-bg-secondary)] border border-[var(--color-border-primary)] text-[var(--color-text-primary)]"
                >
                  <option value="">All types</option>
                  {Object.entries(CONTRACT_TYPE_CONFIG).map(([key, config]) => (
                    <option key={key} value={key}>{config.label}</option>
                  ))}
                </select>
              </div>
              <div className="flex-1 overflow-y-auto p-3 space-y-2">
                {isLoading && (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-[var(--color-text-secondary)]" />
                  </div>
                )}
                {error && <div className="text-xs text-red-400 p-2">Failed to load contracts</div>}
                {contractsList && contractsList.items.length === 0 && (
                  <div className="text-xs text-[var(--color-text-tertiary)] text-center py-8">No contracts found</div>
                )}
                {contractsList?.items.map((contract) => (
                  <ContractCard
                    key={contract.id}
                    contract={contract}
                    isSelected={selectedContractId === contract.id}
                    onClick={() => setSelectedContractId(contract.id)}
                  />
                ))}
              </div>
              {contractsList && (
                <div className="p-2 border-t border-[var(--color-border-primary)] text-[10px] text-[var(--color-text-tertiary)] text-center">
                  {contractsList.total} contract{contractsList.total !== 1 ? 's' : ''}
                </div>
              )}
            </div>

            {/* Right: Contract Detail */}
            <div className="flex-1 min-w-0">
              {selectedContractId ? (
                <ContractDetailPanel contractId={selectedContractId} />
              ) : (
                <div className="flex items-center justify-center h-full text-sm text-[var(--color-text-tertiary)]">
                  Select a contract to view details
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
